import { useRef, useState, type FC } from "react";
import {
  Flex,
  Modal,
  Ruler,
  Slides,
  type SlidesRef,
  Spacing,
  Text,
  type ModalProps,
} from "@huub-web/ui";

import type { AnalysisObservationCategory } from "../../../types";
import { AnalysisObservationsHeader } from "./AnalysisObservationsHeader";
import { AnalysisListItem } from "./AnalysisListItem";

/**
 * @deprecated
 */
export type AnalysisObservationsModalProps = ModalProps & {
  categories: AnalysisObservationCategory[];
  defaultIndex: number;
};

/**
 * @deprecated
 */
export const AnalysisObservationsModal: FC<AnalysisObservationsModalProps> = ({
  categories,
  escape: escapable = true,
  closeable = true,
  size = "large",
  defaultIndex = 0,
  ...props
}) => {
  const slidesRef = useRef<SlidesRef>(null);
  const [currentIndex, setCurrentIndex] = useState(defaultIndex);

  if (!categories || categories.length === 0) {
    return null;
  }

  const totalObservations = categories.length;

  /**
   * Handle navigation to next slide with wrapping
   */
  const handleNext = () => {
    slidesRef.current?.next();
  };

  /**
   * Handle navigation to previous slide with wrapping
   */
  const handlePrevious = () => {
    slidesRef.current?.previous();
  };

  /**
   * Sync state when slide changes (for button labels)
   */
  const handleSlideChange = (slideKey: string) => {
    const index = categories.findIndex((obs) => obs.id === slideKey);
    if (index !== -1) {
      setCurrentIndex(index);
    }
  };

  // Calculate previous and next indices with wrapping
  const previousIndex =
    currentIndex === 0 ? totalObservations - 1 : currentIndex - 1;
  const nextIndex = (currentIndex + 1) % totalObservations;

  // Get the observation titles for button labels
  const previousLabel = categories[previousIndex]?.name || "";
  const nextLabel = categories[nextIndex]?.name || "";

  const currentLabel = categories[currentIndex]?.name || "";

  return (
    <Modal
      escape={escapable}
      closeable={closeable}
      title={
        <Flex direction="column">
          <Spacing height="s" />
          <Text variant="surfaceSubtitle" color="currentColor50" align="center">
            Inzichten per onderdeel
          </Text>
        </Flex>
      }
      size={size}
      {...props}
    >
      <AnalysisObservationsHeader
        currentLabel={currentLabel}
        previousLabel={previousLabel}
        nextLabel={nextLabel}
        handlePrevious={handlePrevious}
        handleNext={handleNext}
        currentIndex={currentIndex}
        previousIndex={previousIndex}
        nextIndex={nextIndex}
        headingVariant="default"
      />

      <Ruler />

      <Slides
        ref={slidesRef}
        slides={Object.fromEntries(
          categories.map((category) => [
            category.id,
            <AnalysisObservationsModalContent
              key={category.id}
              category={category}
            />,
          ]),
        )}
        onSlideChange={handleSlideChange}
        initialSlide={categories[currentIndex].id}
      />
    </Modal>
  );
};

const AnalysisObservationsModalContent: FC<{
  category: AnalysisObservationCategory;
}> = ({ category }) => (
  <Flex direction="column" gap="s">
    <Spacing />
    {category.observations?.map((observation, i) => (
      <AnalysisListItem
        key={i}
        observation={observation}
        alternate={i % 2 === 0}
      />
    ))}
    <Spacing />
  </Flex>
);
